import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

const VITALIS_LOGO = "https://vitalis-sports.ch/wp-content/uploads/2024/12/logo_web.png";

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const { login } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success(t('loginSuccess'));
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Hero Section */}
      <div 
        className="login-hero hidden md:block"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1200&q=80')`
        }}
      >
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <div>
            <img src={VITALIS_LOGO} alt="Vitalis Sports" className="h-12" />
          </div>
          <div className="max-w-md">
            <h1 className="text-4xl mb-4 text-white">
              {language === 'de' ? 'TEAMPLANUNG LEICHT GEMACHT' : 
               language === 'en' ? 'TEAM SCHEDULING MADE EASY' : 
               'PLANIFICATION D\'ÉQUIPE SIMPLIFIÉE'}
            </h1>
            <p className="text-white/80">
              {language === 'de' ? 'Planung, Abwesenheiten, Statistiken — alles an einem Ort.' :
               language === 'en' ? 'Scheduling, absences, statistics — all in one place.' :
               'Planning, absences, statistiques — tout en un seul endroit.'}
            </p>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="md:hidden flex justify-center mb-8">
            <img src={VITALIS_LOGO} alt="Vitalis Sports" className="h-12" />
          </div>

          {/* Language Selector */}
          <div className="flex justify-center gap-2 mb-8">
            {['fr', 'en', 'de'].map((lang) => (
              <button
                key={lang}
                onClick={() => setLanguage(lang)}
                className={`lang-btn ${language === lang ? 'active' : ''}`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="mb-8">
            <h2 className="text-3xl mb-2">{t('login')}</h2>
            <p className="text-muted-foreground text-sm">
              {t('accessWorkspace')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                placeholder=""
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="login-password-input"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full"
            >
              {loading ? t('loading') : t('signIn')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/forgot-password"
              data-testid="forgot-password-link"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              {t('forgotPassword')}
            </Link>
          </div>
          
          {/* Version number */}
          <div className="mt-8 text-center text-xs text-muted-foreground">
            {t('version')} 1.2.0
          </div>
        </div>
      </div>
    </div>
  );
}
